import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Chip, Modal, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Loader from '../../components/common/Loader/Loader';
import UserProfileMainPage from '../../components/user-profile-main/UserProfileMainPage';
import ErrorBoundary from '../../components/common/ErrorBoundary';
import { getMemberMessageHistory, getMemberConversations, getMemberConversationThread } from '../../api/messagingActivity';
// The bubble renderer the Member Engagement drawer uses, so a media message reads
// "\ud83c\udf9e\ufe0f Voice message" here exactly as it does there rather than printing the raw
// placeholder text the chat store keeps in `message`.
import { renderMsgContent } from '../member-engagement-container/memberEngagementUi';
import styles from './styles';
import { sampleHistory } from './sampleData';
import { STATUS_DESCRIPTION, statusLabel } from '../../utils/memberStatus';

/**
 * Member Messaging Activity — per-member history (detail page, v6 design).
 *
 * Native render scoped under `.favor-ma`. Opened from a row / "View history" on
 * the list. The v6 design frames the member's history by PEOPLE and by REPLIES:
 *  - two reply-split metric cards that mirror the list row cell for cell — headline
 *    is DISTINCT PEOPLE (the list's big number), sub-line is the CONVERSATION split
 *    the reply rate is computed over (the list's "N of M" cell and its rate column),
 *    with median reply times,
 *  - a "Conversations by Month" panel counting PEOPLE (not messages) as two
 *    stacked series — inbound (she replied vs awaiting) and outbound (they
 *    replied vs no reply),
 *  - a conversation list with initiated / replied badges.
 *
 * Live data: GET /admin/analytics/stat/getMemberMessageHistory/:id. Falls back
 * to the mockup sample when the endpoint is unavailable. The period aggregate is
 * read from the API's `periods` block when present and otherwise derived from the
 * monthly series, so the new `1mo` / `6mo` periods and per-period `orep` work
 * even before the backend adds them (see API_CHANGES.md).
 *
 * SCOPE: the page opens on ALL TIME, because the list page it is opened from reports
 * all-time figures. Every block is computed live — and the list recomputes its visible
 * rows live too, with the same helpers over the same window — so a row and this page
 * agree because they are the same computation, not because they share a cache.
 */
// `all` leads and is the default: the Messaging Activity list this page is opened
// from reports all-time figures, so the first thing an admin sees here has to be the
// same scope as the row they just clicked. Opening on 12 MO made every card look like
// it disagreed with the list for any member with older activity.
const PERIODS = [['all', 'ALL TIME'], ['7d', '7D'], ['1mo', '1 MO'], ['3mo', '3 MO'], ['6mo', '6 MO'], ['12mo', '12 MO']];
const PERIOD_LABEL = { all: 'all time', '7d': '7 days', '1mo': '1 month', '3mo': '3 months', '6mo': '6 months', '12mo': '12 months' };
// Trailing months each period highlights in the chart and aggregates over. `all` spans
// the whole 13-month series the API returns, so nothing is dimmed.
const PERIOD_MONTHS = { all: 13, '7d': 1, '1mo': 1, '3mo': 3, '6mo': 6, '12mo': 12 };

const num = (n) => (n || 0).toLocaleString();
const pct = (a, b) => (b ? Math.round((a / b) * 100) : null);
const pronoun = (g) => (g === 'F'
  ? { subj: 'she', Subj: 'She', obj: 'her', Obj: 'Her', poss: 'her' }
  : g === 'M'
    ? { subj: 'he', Subj: 'He', obj: 'him', Obj: 'Him', poss: 'his' }
    : { subj: 'they', Subj: 'They', obj: 'them', Obj: 'Them', poss: 'their' });
const AVA_COLORS = ['#3b5bdb', '#d9885c', '#9c6ade', '#5cae9c', '#bf6a8f', '#6a86bf', '#c4a05c', '#5cc4a0'];
const avaColor = (s) => AVA_COLORS[[...String(s)].reduce((a, c) => a + c.charCodeAt(0), 0) % AVA_COLORS.length];
const fmtJoined = (isoStr) => (isoStr ? new Date(isoStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fmtMin = (x) => {
  if (x == null) return '—';
  if (x < 60) return `${x}m`;
  if (x < 1440) return `${Math.round((x / 60) * 10) / 10}h`;
  return `${Math.round((x / 1440) * 10) / 10}d`;
};
// Inside an open thread the DATE is carried by a separator between days, so each
// message only needs its clock time. A thread can still span months, which is exactly
// why that separator exists: messages a screen apart used to read as one exchange.
const fmtClock = (isoStr) => (isoStr
  ? new Date(isoStr).toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true })
  : '');
const dayKey = (isoStr) => (isoStr ? new Date(isoStr).toDateString() : '');
const fmtDay = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const today = new Date();
  const yest = new Date(today.getTime() - 864e5);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yest.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', ...(d.getFullYear() === today.getFullYear() ? {} : { year: 'numeric' }),
  });
};
const ago = (isoStr) => {
  if (!isoStr) return '—';
  const d = Math.floor((Date.now() - new Date(isoStr).getTime()) / 864e5);
  if (d <= 0) return 'today';
  if (d === 1) return '1 day ago';
  if (d < 30) return `${d} days ago`;
  if (d < 365) return `${Math.round(d / 30)} mo ago`;
  return `${Math.round(d / 365)}y ago`;
};

const MemberHistory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [statusFlag, setStatusFlag] = useState('loading');
  const [fetching, setFetching] = useState(true);
  const [period, setPeriod] = useState('all');
  const [tab, setTab] = useState('all');
  const [convShown, setConvShown] = useState(6);
  // The profile modal is shared by the header button and every conversation row, so it
  // holds WHOSE profile is open rather than a boolean. `{ id, name }`; null = closed.
  //
  // Deleted partners are opened the same way as anyone else: the row already knows the
  // partner's ORIGINAL user id (the delete log keys its dump on `refId`, and
  // `GET /admin/user/:id` resolves either id), so UserProfileMainPage reads the flag off
  // the payload and renders the archived copy read-only. The only rows that can't resolve
  // are members missing from BOTH collections — the API labels those "Deleted User", and
  // the profile component's own empty state explains it.
  const [profileUser, setProfileUser] = useState(null);
  const openProfile = (e, u) => {
    if (e) e.stopPropagation();
    if (u?.id) setProfileUser(u);
  };
  const closeProfile = () => setProfileUser(null);
  // The conversation panel is windowed by the SAME period selector as the cards, but it
  // lives in its own state because it is the only block that needs a round trip to
  // change: the metric blocks for every period ship in the initial payload.
  // `window` records which period the held list is FOR, which is what stops the refetch
  // effect below from firing for a list the page already has.
  const [convs, setConvs] = useState(null); // { conversations, total, truncated, window }
  const [convLoading, setConvLoading] = useState(false);
  // The expanded conversation, by PARTNER id, and the transcripts already fetched.
  // One row is open at a time: these threads run to hundreds of messages, and two of
  // them open at once turns the panel into a scroll no one can navigate.
  const [openConv, setOpenConv] = useState(null);
  // partnerId -> { loading } | { error } | { messages, total, truncated }
  const [threads, setThreads] = useState({});
  // Transcripts are windowed by the SAME period as the list, so a reply that arrives
  // here on a late response must not be written into a panel that has since moved to
  // another window (or another member). Both are captured per request and checked
  // against these refs before the result is kept.
  const periodRef = React.useRef(period);
  const idRef = React.useRef(id);
  useEffect(() => { periodRef.current = period; }, [period]);
  useEffect(() => { idRef.current = id; }, [id]);

  // A transcript belongs to one (member, period) pair — the same conversation shows
  // fewer messages under 7D than under ALL TIME — so the cache is dropped whenever
  // either changes rather than served for a window it was not fetched for.
  useEffect(() => { setThreads({}); setOpenConv(null); }, [id, period]);

  const loadThread = async (c, force = false) => {
    const key = String(c.id);
    const held = threads[key];
    if (!force && held && !held.error) return;
    // The mockup fixture carries counts only; there is no endpoint to ask.
    if (statusFlag !== 'live') { setThreads((t) => ({ ...t, [key]: { sample: true } })); return; }
    const forPeriod = period;
    const forId = id;
    setThreads((t) => ({ ...t, [key]: { loading: true } }));
    try {
      const res = await getMemberConversationThread(forId, c.id, forPeriod);
      if (periodRef.current !== forPeriod || idRef.current !== forId) return;
      setThreads((t) => ({ ...t, [key]: res ? { ...res } : { error: true } }));
    } catch (err) {
      if (periodRef.current !== forPeriod || idRef.current !== forId) return;
      setThreads((t) => ({ ...t, [key]: { error: true } }));
    }
  };

  const toggleConv = (c) => {
    const key = String(c.id);
    if (openConv === key) { setOpenConv(null); return; }
    setOpenConv(key);
    loadThread(c);
  };

  // Chat Messenger is organised BOT-first: pick a bot on the left, then one of the
  // people it is talking to. So a conversation can only be opened there when the
  // partner is the bot -- for a member-to-member thread there is no column to land
  // in, and the link is not offered on those rows.
  //
  // Rendered as a real <a target="_blank">, not a scripted window.open: replying in
  // Chat Messenger is a separate job from reading this report, and navigating in place
  // would throw away the member's history (period, tab, how far the list was paged) to
  // come back to. A genuine href also keeps middle-click / ctrl-click working and is
  // not what pop-up blockers stop.
  const chatHref = (c) => `/chat?bot=${encodeURIComponent(c.id)}&member=${encodeURIComponent(id)}`;

  const takeConvs = (res, forPeriod) => setConvs({
    conversations: res.conversations || [],
    total: res.conversationsTotal ?? (res.conversations || []).length,
    truncated: !!res.conversationsTruncated,
    // Split of the whole window, from the server. Counting the loaded list instead made
    // the tabs under-report whenever it was capped — Rishbh's card said 76 conversations
    // started while the tab beneath it said 75, because the 81st thread was never shipped.
    byDir: res.conversationsByDir || null,
    // The period we ASKED for, never the one echoed back. The server coerces an
    // unrecognised period to 'all', so echoing it would make `convs.window === period`
    // permanently false the day someone adds a pill the backend does not know yet —
    // and the refetch effect below would then loop forever. Recording the request keeps
    // the loop terminating whatever the server says.
    window: forPeriod,
  });

  useEffect(() => {
    let active = true;
    setFetching(true);
    setConvShown(6);
    (async () => {
      try {
        // Ask for the conversation list already scoped to the period the page opens on,
        // so the first paint is consistent and costs no extra request.
        const res = await getMemberMessageHistory(id, period);
        if (!active) return;
        if (res && res.member && res.periods) { setData(res); takeConvs(res, period); setStatusFlag('live'); }
        else { const s = sampleHistory(id); setData(s); takeConvs(s, period); setStatusFlag('placeholder'); }
      } catch (err) {
        if (active) { const s = sampleHistory(id); setData(s); takeConvs(s, period); setStatusFlag('placeholder'); }
      } finally {
        if (active) setFetching(false);
      }
    })();
    return () => { active = false; };
    // Deliberately NOT keyed on `period`: changing the period must not reload the whole
    // page (five metric windows and a 13-month series recomputed to change one list,
    // with the full-page loader flashing on every click). The effect below refetches
    // just the panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    // Sample mode has no endpoint to call; it keeps whatever the fixture provided.
    if (statusFlag !== 'live' || !convs || convs.window === period) return undefined;
    let active = true;
    setConvLoading(true);
    setConvShown(6);
    (async () => {
      try {
        const res = await getMemberConversations(id, period);
        if (active && res) takeConvs(res, period);
      } catch (err) {
        // Keep the list we have rather than blanking the panel on a transient failure.
      } finally {
        if (active) setConvLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, id, statusFlag, convs]);

  const member = data?.member;
  const months = useMemo(() => data?.months || [], [data]);
  const G = pronoun(member?.gender);

  // Period aggregate. Prefer the API's precomputed block; alias 1mo→30d for the
  // current backend; otherwise derive from the monthly series so the new 1mo/6mo
  // periods work before the API ships them. Fill orep from months if absent.
  const agg = useMemo(() => {
    const blocks = data?.periods || {};
    let a = blocks[period] || (period === '1mo' ? blocks['30d'] : null);
    if (!a) {
      // Fallback for a backend that predates a period block. It must carry the SAME KEY
      // SET a server block does. The cards read `agg.outboundThreads ?? agg.pm`, so a
      // derived object that omitted the conversation counts silently substituted a
      // PEOPLE count into a card whose percentage is computed over CONVERSATIONS — the
      // two then disagreed with each other on the same card (11 people, 9% of 11
      // conversations). Summing monthly thread counts double-counts a thread active in
      // more than one month, so this stays a last resort; the API ships every period
      // the UI offers.
      const n = PERIOD_MONTHS[period] || 12;
      const slice = months.slice(Math.max(0, months.length - n));
      const sum = (k) => slice.reduce((acc, m) => acc + (m[k] || 0), 0);
      const inboundThreads = sum('inboundThreads');
      const outboundThreads = sum('outboundThreads');
      const outboundReplied = sum('outboundReplied');
      const replied = sum('replied');
      a = {
        pm: sum('peopleOut'), pmh: sum('peopleIn'), ms: sum('outMsgs'), mr: sum('inMsgs'),
        orep: sum('repliedOut'), likes: sum('likes'),
        inboundThreads, reached: inboundThreads, replied, irep: replied,
        rr: inboundThreads ? Math.round((replied / inboundThreads) * 100) : 0,
        outboundThreads, oreached: outboundThreads, outboundReplied,
        orr: outboundThreads ? Math.round((outboundReplied / outboundThreads) * 100) : null,
      };
    }
    if (a.orep == null) {
      const n = PERIOD_MONTHS[period] || 12;
      a = { ...a, orep: months.slice(Math.max(0, months.length - n)).reduce((acc, m) => acc + (m.repliedOut || 0), 0) };
    }
    return a;
  }, [data, period, months]);

  // Tabs bucket each conversation by who INITIATED it (`dir`): threads the member
  // opened are "she messaged", threads the other person opened are "she receives".
  // The buckets are mutually exclusive, so out + in === all.
  const convList = useMemo(() => convs?.conversations || [], [convs]);
  const conversations = useMemo(
    () => convList.filter((c) => (tab === 'all' ? true : c.dir === tab)).slice().sort((a, b) => new Date(b.lastTs) - new Date(a.lastTs)),
    [convList, tab],
  );
  // Prefer the server's split of the WHOLE window; fall back to counting what was loaded
  // only when an older payload has no `conversationsByDir`.
  const counts = useMemo(() => {
    const loaded = { all: convList.length, out: convList.filter((c) => c.dir === 'out').length, in: convList.filter((c) => c.dir === 'in').length };
    if (!convs?.byDir) return loaded;
    return { all: convs.total ?? loaded.all, out: convs.byDir.out ?? loaded.out, in: convs.byDir.in ?? loaded.in, loaded };
  }, [convList, convs]);

  if (fetching) {
    return (
      <Box sx={{ pb: 4 }}>
        <style>{styles}</style>
        <div className="favor-ma"><div style={{ minHeight: 'calc(100vh - 160px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Loader /></div></div>
      </Box>
    );
  }
  if (!member) return null;

  const n = PERIOD_MONTHS[period];
  const firstIncl = months.length - n;
  // ---------------------------------------------------------------------------
  // ONE measure across this whole tab: CONVERSATIONS, split by who sent the thread's
  // first message, plus the reply rate over them. The distinct-people counts (pm/pmh)
  // are still returned by the API but deliberately NOT rendered anywhere — user's call,
  // 2026-07-27, after the two measures side by side were reported as a discrepancy on
  // four separate members. A member who mostly replies has messaged far more people than
  // they have conversations (Mukul: 37 people, 11 started), and no amount of labelling
  // stopped that reading as a contradiction. Do not put them back on a card or a column.
  const outN = agg.outboundThreads ?? agg.oreached ?? 0;
  const outRep = agg.outboundReplied ?? 0;
  const inN = agg.inboundThreads ?? agg.reached ?? 0;
  const inRep = agg.replied ?? agg.irep ?? 0;
  // Prefer the server's own rounding over recomputing it, so the percentage on this
  // page is the identical integer the list column prints.
  const outPct = agg.orr != null ? agg.orr : pct(outRep, outN);
  const inPct = agg.rr != null ? agg.rr : pct(inRep, inN);
  // Median reply times are per-period too. `member.med` is the all-time pair (what the
  // list shows); reading it for every period made a 7-day card quote an all-time median.
  const outMed = agg.outMin ?? member.med?.outMin ?? null;
  const inMed = agg.inMin ?? member.med?.inMin ?? null;
  const scoped = period === 'all' ? '' : ' in this period';
  const chartTitle = months.length ? `Conversations by Month — ${months[0].label} ${months[0].y} to ${months[months.length - 1].label} ${months[months.length - 1].y}` : 'Conversations by Month';

  // One stacked people-series: solid = replied, light = the remainder (awaiting /
  // no reply). Bars count people, months outside the period are dimmed, current
  // month outlined in gold.
  const renderSeries = (pairs, cls, solidVar, lightVar) => {
    const H = 148;
    const hmax = Math.max(1, ...pairs.map((p) => p[0]));
    return (
      <div className="chart">
        {pairs.map((p, i) => {
          const [totalP, repd] = p;
          let sh = Math.round((repd / hmax) * H);
          let lh = Math.round(((totalP - repd) / hmax) * H);
          if (repd > 0 && sh < 3) sh = 3;
          if (totalP - repd > 0 && lh < 2) lh = 2;
          const m = months[i];
          const showYear = i === 0 || i === months.length - 1 || m.label === 'Jan';
          return (
            <div className={`mcol ${i < firstIncl ? 'dim' : ''} ${i === months.length - 1 ? 'cur' : ''}`} key={`${m.y}-${m.m}`}>
              <div className="mbars" title={`${m.label} ${m.y}: ${totalP} conversations · ${repd} replied`}>
                <div className="vbar">
                  <span className={`cnt ${cls}`}>{totalP}</span>
                  <div className="seg" style={{ height: sh, background: solidVar, ...(lh === 0 ? { borderRadius: '5px 5px 0 0' } : {}) }}>{sh >= 16 ? <span className="sn">{repd}</span> : null}</div>
                  <div className="seg top" style={{ height: lh, background: lightVar }} />
                </div>
              </div>
              <div className="mlab">{m.label}{showYear ? <small>{m.y}</small> : null}</div>
            </div>
          );
        })}
      </div>
    );
  };

  // One expanded transcript, laid out as the conversation it was: the partner on the
  // left, the member on the right — the same reading the Member Engagement drawer
  // gives. Runs from one side are grouped under a single avatar, and every change of
  // calendar day gets a separator, because these threads routinely span weeks and an
  // ungrouped, undated wall of boxes reads as one sitting however far apart the
  // messages actually were.
  //
  // The transcript is the PAIR, over the same window as the row. The row's "N msgs" is
  // per ROOM (the API groups threads on OneToOneId), so the two counts can legitimately
  // differ: a room with three people counts the member's legs with the third party into
  // one row, and a pair spread over two rooms is listed as two rows. `total` from the
  // API is the true number for this pair — so it is stated above the transcript whenever
  // it does not match the row, as well as when the thread is longer than the cap. Saying
  // nothing is what would make the panel look like it contradicts itself.
  const renderThread = (c) => {
    const t = threads[String(c.id)];
    if (!t || t.loading) return <div className="tmsg">Loading conversation…</div>;
    if (t.sample) return <div className="tmsg">Message content is not available for sample data.</div>;
    if (t.error) {
      return (
        <div className="tmsg">
          This conversation couldn’t be loaded.{' '}
          <span className="clink" role="button" tabIndex={0} onClick={() => loadThread(c, true)}>Retry</span>
        </div>
      );
    }
    const msgs = t.messages || [];
    if (!msgs.length) return <div className="tmsg">No messages in this period.</div>;
    return (
      <>
        {t.truncated ? (
          <div className="tnote">Showing the last {num(msgs.length)} of {num(t.total)} messages</div>
        ) : t.total !== c.msgs ? (
          <div className="tnote" title="The row counts every message in the chat room; a room can hold more than two people, and a pair can span two rooms.">
            {num(t.total)} {t.total === 1 ? 'message' : 'messages'} with {c.name} · the row counts the whole room
          </div>
        ) : null}
        {/* The transcript scrolls in place. A 200-message thread rendered at full height
            pushes every conversation below it several screens down, so opening one row
            near the top of the list buries the rest of the list. */}
        <div className="tscroll">
        {msgs.map((m, i) => {
          const prev = i > 0 ? msgs[i - 1] : null;
          const newDay = !prev || dayKey(prev.t) !== dayKey(m.t);
          // Same speaker, same day, close in time: one run, one avatar, one name. Half
          // an hour later is a new turn even from the same person, and reads better
          // with the name repeated.
          const run = !newDay && !!prev && prev.f === m.f
            && (new Date(m.t) - new Date(prev.t)) < 30 * 60000;
          const mine = m.f === 'me';
          const who = mine ? (member?.name || 'Member') : c.name;
          const pic = mine ? member?.profilePicture : c.profilePicture;
          const inits = mine ? member?.initials : c.initials;
          return (
            <React.Fragment key={`${m.t}-${i}`}>
              {newDay ? <div className="tday"><span>{fmtDay(m.t)}</span></div> : null}
              <div className={`trow ${mine ? 'me' : 'them'}${run ? ' run' : ''}`}>
                <div className="tav" style={{ background: avaColor(mine ? member?.id : c.id) }}>
                  {pic ? <img src={pic} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : inits}
                </div>
                <div className="tb">
                  {!run ? <span className="who">{who}</span> : null}
                  <span className="tx">{renderMsgContent(m)}</span>
                  <time>{fmtClock(m.t)}</time>
                </div>
              </div>
            </React.Fragment>
          );
        })}
        {/* The badge on the row above says NO REPLY / AWAITING; spelling it out at the
            end of the transcript is what makes the last entry read as the end of the
            exchange rather than as a list that was cut short. */}
          {!c.replied ? (
            <div className="twait">{c.dir === 'out' ? 'No reply from this match.' : `${G.Subj} never replied.`}</div>
          ) : null}
        </div>
        {/* Long threads push the row's own chevron far off screen, so the way out is
            repeated at the end of what you were reading. */}
        <div className="tclose">
          <span className="clink" role="button" tabIndex={0} onClick={() => setOpenConv(null)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenConv(null); } }}>Collapse</span>
        </div>
      </>
    );
  };

  return (
    <Box sx={{ pb: 4 }}>
      <style>{styles}</style>
      <div className="favor-ma">
        <div className="back" role="button" tabIndex={0} onClick={() => navigate('/messaging-activity')} onKeyDown={(e) => { if (e.key === 'Enter') navigate('/messaging-activity'); }}><span className="ar" /> Back to <b>Messaging Activity</b></div>
        {statusFlag === 'placeholder' && (
          <div style={{ marginBottom: 14 }}><Chip size="small" color="warning" variant="outlined" label="Showing sample data — live source unavailable" /></div>
        )}

        {/* header */}
        <div className="phead">
          <div className="pava" style={{ background: avaColor(member.id) }}>
            {member.profilePicture ? <img src={member.profilePicture} alt="" /> : member.initials}
            {member.verified && <span className="vb" />}
          </div>
          <div className="pinfo">
            <div className="pname">{member.name}{member.age ? <span className="age">, {member.age}</span> : null}
              {member.gender === 'F' ? <span className="gchip f">Female</span> : member.gender === 'M' ? <span className="gchip m">Male</span> : <span className="gchip o">Other</span>}
            </div>
            <div className="badges">
              {member.verified && <span className="bdg ver">✓ Verified</span>}
              <span className="bdg mem">{member.tier}</span>
              {member.account && <span className={`acct-st ${member.account}`} title={STATUS_DESCRIPTION[member.account]}><i />{statusLabel(member.account)}</span>}
              <span className="bdg act">● Active {ago(member.lastActive)}</span>
              {member.joinedDaysAgo != null && member.joinedDaysAgo <= 7 && <span className="bdg new">New member</span>}
            </div>
            <div className="pmeta">
              <div className="mi"><div className="l">Member ID</div><div className="v">#{String(member.id).slice(-6).toUpperCase()}</div></div>
              <div className="mi"><div className="l">Location</div><div className="v">{member.city || '—'}{member.country ? `, ${member.country}` : ''}</div></div>
              <div className="mi"><div className="l">Joined</div><div className="v">{fmtJoined(member.joined)} <small>· {member.joinedDaysAgo != null ? `${member.joinedDaysAgo}d ago` : ''}</small></div></div>
              <div className="mi"><div className="l">Profile likes</div><div className="v">{num(member.likesTotal)} <small>· lifetime</small></div></div>
            </div>
          </div>
          <div className="pactions">
            <div className="pbtn dark" onClick={() => openProfile(null, { id: member.id, name: member.name })}>View Full Profile</div>
            <div className="privnote"><span>🔒</span> Message content is private. Admins see activity counts only.</div>
          </div>
        </div>

        {/* period bar */}
        <div className="periodbar">
          <span className="lab">Showing data for</span>
          <div className="segs">
            {PERIODS.map(([k, l]) => (<button key={k} type="button" className={`segp ${period === k ? 'on' : ''}`} onClick={() => setPeriod(k)}>{l}</button>))}
          </div>
          <div className="daterange"><b>{PERIOD_LABEL[period]}</b></div>
        </div>


        {/* metric cards — two reply-split tiles */}
        <div className="mcards two">
          {/* Headline is the CONVERSATION count — the same measure as the tabs below, the
              charts, the conversation total and the list's columns. It used to be distinct
              PEOPLE, which is a different number (a member who mostly replies has messaged
              far more people than they have conversations: Mukul, 37 people vs 11 started).
              Two measures side by side under similar words read as a contradiction no
              matter how carefully each is labelled, so there is now ONE kind of big number
              on this tab and the people count is secondary text. */}
          <div className="mc o">
            <span className="scope">{PERIODS.find(([k]) => k === period)[1]}</span><span className="cbar" />
            <div className="k">Conversations {G.Subj} Started</div>
            <div className="v">{num(outN)}</div>
            <div className="dd2">{outN
              ? `${num(outRep)} got a reply · ${outPct}%${outMed != null ? ` · reply in ${fmtMin(outMed)}` : ''}`
              : `${G.Subj} has not started a conversation${scoped}`}</div>
          </div>
          <div className="mc b">
            <span className="scope">{PERIODS.find(([k]) => k === period)[1]}</span><span className="cbar" />
            <div className="k">Conversations Started With {G.Obj}</div>
            <div className="v">{num(inN)}</div>
            <div className="dd2">{inN
              ? `${G.Subj} replied to ${num(inRep)} · ${inPct}%${inMed != null ? ` · in ${fmtMin(inMed)}` : ''}`
              : `Nobody has started a conversation with ${G.obj}${scoped}`}</div>
          </div>
        </div>

        {/* conversations by month — people, not messages */}
        <div className="yearpanel">
          <div className="yphead">
            <div className="t">{chartTitle}</div>
            <div className="pmeta-tag">CONVERSATIONS · NOT MESSAGES</div>
          </div>
          {/* The bars plot `inboundThreads` / `outboundThreads` — CONVERSATIONS, split
              by who sent the thread's first message. They were captioned as people,
              which is a different measure (one person can hold several threads) and
              the one the cards' headline numbers use. */}
          <div className="ycap">Bars count conversations, not messages or people. The solid part of each bar is conversations that got a reply. Months outside the selected period are dimmed; the current month is outlined in gold.</div>

          <div className="chblock">
            <div className="chh">
              <span className="cht">Conversations started with {G.obj}</span>
              <span className="chl"><i className="sw" style={{ background: 'var(--blue)' }} />{G.subj} replied&nbsp;&nbsp;<i className="sw" style={{ background: 'var(--blue-lt)' }} />awaiting {G.poss} reply</span>
            </div>
            <div className="chartwrap">{renderSeries(months.map((m) => [m.inboundThreads ?? m.peopleIn, m.replied]), 'ci', 'var(--blue)', 'var(--blue-lt)')}</div>
          </div>

          <div className="chblock">
            <div className="chh">
              <span className="cht">Conversations {G.subj} started</span>
              <span className="chl"><i className="sw" style={{ background: 'var(--orange)' }} />they replied&nbsp;&nbsp;<i className="sw" style={{ background: 'var(--orange-lt)' }} />no reply</span>
            </div>
            <div className="chartwrap">{renderSeries(months.map((m) => [m.outboundThreads ?? m.peopleOut, m.outboundReplied ?? m.repliedOut ?? 0]), 'co', 'var(--orange)', 'var(--orange-lt)')}</div>
          </div>
        </div>

        {/* conversations */}
        <div className="panel">
          {/* The header used to count the conversations it had RECEIVED and call that the
              total — but the API caps the list at 80, so a member with 304 threads read
              "80 PEOPLE · IN PERIOD" directly under cards saying 269 and 35.
              `conversationsTotal` is the true count in the window. */}
          <div className="ph">
            <div className="t">Conversations{convLoading ? <span style={{ fontSize: 12, fontWeight: 400, color: '#9ca3af' }}> · updating…</span> : null}</div>
            <div className="c">{num(convs?.total ?? counts.all)} CONVERSATIONS · {String(PERIOD_LABEL[period]).toUpperCase()}</div>
          </div>
          <div className="tabs">
            {/* Named by INITIATOR, matching the chart headings and the card sub-lines.
                These tabs count CONVERSATIONS but used to be labelled "He messaged" /
                "Messaged him" — the same words as the card headlines, which count
                PEOPLE. So a member read "People He Messaged 6" above a tab reading
                "He messaged (2)" and the page looked like it contradicted itself. */}
            <span className={`tab ${tab === 'all' ? 'on' : ''}`} onClick={() => { setTab('all'); setConvShown(6); }}>All ({counts.all})</span>
            <span className={`tab ${tab === 'out' ? 'on' : ''}`} onClick={() => { setTab('out'); setConvShown(6); }}>{G.Subj} started ({counts.out})</span>
            <span className={`tab ${tab === 'in' ? 'on' : ''}`} onClick={() => { setTab('in'); setConvShown(6); }}>Started with {G.obj} ({counts.in})</span>
          </div>
          <div className="convlist">
            {conversations.slice(0, convShown).map((c) => {
              const initTxt = c.dir === 'out' ? `${G.subj} initiated` : `${c.gender === 'F' ? 'she' : c.gender === 'M' ? 'he' : 'they'} initiated`;
              const badge = c.replied
                ? <span className="cdir ok">{c.dir === 'in' ? `${G.Subj.toUpperCase()} ` : ''}REPLIED</span>
                : c.dir === 'out'
                  ? <span className="cdir no">NO REPLY</span>
                  : <span className="cdir wait">AWAITING {G.Obj.toUpperCase()}</span>;
              const key = String(c.id);
              const isOpen = openConv === key;
              return (
                // The row EXPANDS the conversation; the profile is opened by its own
                // link inside it. It used to be the other way round — the whole row went
                // to the profile modal — but the panel only ever said whether a thread
                // was answered, never what was said in it, so "replied / no reply" could
                // not be checked against the exchange it describes. Both actions are
                // still one click; only which one owns the row changed.
                <div className={`cvitem${isOpen ? ' open' : ''}`} key={key}>
                  <div
                    className="crow open"
                    role="button"
                    tabIndex={0}
                    aria-expanded={isOpen}
                    onClick={() => toggleConv(c)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleConv(c); } }}
                  >
                    <div className="avat" style={{ background: avaColor(c.id) }}>
                      {c.profilePicture ? <img src={c.profilePicture} alt="" /> : c.initials}
                    </div>
                    <div className="cwho">
                      <div className="cname">{c.name}{c.age ? `, ${c.age}` : ''} {c.isBot ? <span className="gchip o">Bot</span> : null}{c.isDeleted ? <span className="gchip del">Deleted</span> : null}</div>
                      <div className="csub">
                        First contact {fmtJoined(c.firstTs)} · {initTxt} ·{' '}
                        {/* Rows carry a real user id (bots and deleted members included), so
                            there is no row here whose profile can't be opened. */}
                        <span
                          className="clink"
                          role="button"
                          tabIndex={0}
                          onClick={(e) => openProfile(e, { id: c.id, name: c.name })}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); openProfile(e, { id: c.id, name: c.name }); } }}
                        >View profile</span>
                        {c.isBot && !c.isDeleted ? (
                          <>
                            {' · '}
                            <a
                              className="clink"
                              href={chatHref(c)}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Open this conversation in Chat Messenger (new tab)"
                              onClick={(e) => e.stopPropagation()}
                            >Open in chat</a>
                          </>
                        ) : null}
                      </div>
                    </div>
                    {badge}
                    <div className="cright"><div className="cmsg">{c.msgs}<small>msgs</small></div><div className="clast">{ago(c.lastTs)}</div></div>
                    <svg className="chev" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
                  </div>
                  {isOpen ? <div className="thread">{renderThread(c)}</div> : null}
                </div>
              );
            })}
            {conversations.length === 0 && <div className="crow empty">No conversations in this view.</div>}
          </div>
          <div className="listfoot">
            <span>
              Showing {Math.min(convShown, conversations.length)} of {conversations.length} · most recent
              {/* convList.length is what was LOADED (the server's cap); counts.* are now
                  the true window totals, so they must NOT be used here. */}
              {convs?.truncated ? ` · list capped at the ${num(convList.length)} most recent of ${num(convs.total)}` : ''}
            </span>
            {convShown < conversations.length
              ? <span className="loadmore" onClick={() => setConvShown((v) => v + 6)}>Load more ▾</span>
              : <span />}
          </div>
        </div>

        <div className="note">Interactive report on live data · the period selector recomputes every number · conversation tabs filter. Open a conversation to read its messages.</div>
      </div>

      <Modal open={!!profileUser} onClose={closeProfile} sx={{ zIndex: 1500 }}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', maxWidth: 1300, width: '95%', bgcolor: 'background.paper', borderRadius: '20px', boxShadow: 24, p: '0 30px 30px 30px', maxHeight: '80vh', overflow: 'auto' }}>
          <Box sx={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10, padding: '10px', display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton onClick={closeProfile}><CloseIcon /></IconButton>
          </Box>
          {profileUser && statusFlag === 'live' && (
            // Same guard the attribution members list uses: a single malformed field on
            // one profile — the delete-log dumps are sparse, so archived ones are the
            // likeliest — must not white-screen the page behind the modal. Keyed on the
            // id so opening a different row after a failure renders fresh.
            <ErrorBoundary
              resetKey={profileUser.id}
              fallback={(
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <p className="mb-2 fw-medium">This profile couldn’t be displayed.</p>
                  <p className="text-secondary-light mb-3" style={{ fontSize: 13 }}>
                    Some of this member’s data is incomplete or malformed. Other profiles are unaffected.
                  </p>
                </Box>
              )}
            >
              {/* `key` forces a remount per member — UserProfileMainPage fetches once on
                  mount, so reusing the instance for a different id would show the
                  previous member's profile. */}
              <UserProfileMainPage key={profileUser.id} userId={profileUser.id} />
            </ErrorBoundary>
          )}
          {profileUser && statusFlag !== 'live' && <Box sx={{ p: 3 }}>Profile preview is unavailable for sample data.</Box>}
        </Box>
      </Modal>
    </Box>
  );
};

export default MemberHistory;
